import type { AuthResponse, User } from "@types";
import { safeConsole } from "../utils/safeConsole";
import {
  applySafeStorageChanges,
  getSafeStorage,
  readSafeStorage,
  removeSafeStorage,
  writeSafeStorage,
} from "../utils/safeStorage";

const SESSION_KEYS = {
  accessToken: "accessToken",
  companyId: "companyId",
  expiration: "expiration",
  intentionalLogout: "intentionalLogout",
  user: "user",
} as const;

const AUTH_SYNC_STORAGE_KEYS = new Set<string>([
  SESSION_KEYS.accessToken,
  SESSION_KEYS.expiration,
  SESSION_KEYS.user,
]);

export const isAuthSyncStorageKey = (key: string | null): boolean =>
  key === null || AUTH_SYNC_STORAGE_KEYS.has(key);

export type PersistedSessionSnapshot = {
  accessToken: string;
  companyId?: string;
  user?: User;
};

export type PersistedAuthSnapshot = Pick<
  PersistedSessionSnapshot,
  "accessToken" | "user"
>;

export const normalizeSessionUser = (user: User): User => ({
  ...user,
  photoUrl: user.photoUrl ?? null,
});

const readPersistedUser = (clearMalformed: boolean): User | undefined => {
  const userString = readSafeStorage(SESSION_KEYS.user);
  if (!userString) return undefined;

  try {
    return normalizeSessionUser(JSON.parse(userString));
  } catch (error) {
    safeConsole.error(
      "authentication-lifecycle",
      clearMalformed
        ? "Failed to parse user from localStorage, clearing corrupted data"
        : "Failed to parse user from localStorage during initialization",
      error,
    );
    if (clearMalformed) {
      removeSafeStorage(SESSION_KEYS.user);
    }
    return undefined;
  }
};

export const readPersistedSession = (
  clearMalformedUser = false,
): PersistedSessionSnapshot => ({
  accessToken: readSafeStorage(SESSION_KEYS.accessToken) || "",
  companyId: readSafeStorage(SESSION_KEYS.companyId) || undefined,
  user: readPersistedUser(clearMalformedUser),
});

export const readPersistedAuth = (): PersistedAuthSnapshot => ({
  accessToken: readSafeStorage(SESSION_KEYS.accessToken) || "",
  user: readPersistedUser(false),
});

export const readPersistedAccessToken = (): string | null =>
  readSafeStorage(SESSION_KEYS.accessToken);

export const readPersistedCompanyId = (): string | null =>
  readSafeStorage(SESSION_KEYS.companyId);

export const getStoredSessionUser = (): User | undefined =>
  readPersistedUser(false);

const authStorageChanges = (auth: AuthResponse) => {
  const user = normalizeSessionUser(auth.user);
  return [
    { key: SESSION_KEYS.accessToken, value: auth.accessToken },
    { key: SESSION_KEYS.expiration, value: auth.expiration.toString() },
    { key: SESSION_KEYS.user, value: JSON.stringify(user) },
  ];
};

export const persistInteractiveLogin = (auth: AuthResponse): boolean => {
  const user = normalizeSessionUser(auth.user);
  const defaultCompanyId = user.companyIds[0];
  return applySafeStorageChanges([
    ...authStorageChanges(auth),
    { key: SESSION_KEYS.companyId, value: defaultCompanyId ?? null },
    { key: SESSION_KEYS.intentionalLogout, value: null },
  ]);
};

export const persistTokenRefresh = (auth: AuthResponse): boolean =>
  applySafeStorageChanges(authStorageChanges(auth));

export const persistCompanyId = (companyId: string): boolean =>
  applySafeStorageChanges([{ key: SESSION_KEYS.companyId, value: companyId }]);

export const persistUser = (user: User): boolean =>
  applySafeStorageChanges([
    {
      key: SESSION_KEYS.user,
      value: JSON.stringify(normalizeSessionUser(user)),
    },
  ]);

export const clearPersistedSession = (): boolean =>
  applySafeStorageChanges([
    { key: SESSION_KEYS.accessToken, value: null },
    { key: SESSION_KEYS.expiration, value: null },
    { key: SESSION_KEYS.user, value: null },
    { key: SESSION_KEYS.companyId, value: null },
  ]);

export const markIntentionalLogout = (): void => {
  writeSafeStorage(SESSION_KEYS.intentionalLogout, "1");
};

export const wasIntentionalLogout = (): boolean =>
  readSafeStorage(SESSION_KEYS.intentionalLogout) === "1";

export const clearIntentionalLogoutMarker = (): void => {
  removeSafeStorage(SESSION_KEYS.intentionalLogout);
};

const TOKEN_REFRESH_SKEW_SECONDS = 10 * 60;

/**
 * Whether the stored backend access token is missing, or within `skewSeconds`
 * of expiry. An unreadable store returns false so the silent-auth poll cannot
 * spin forever while also being unable to persist a refreshed token.
 */
export const accessTokenNeedsRefresh = (
  skewSeconds: number = TOKEN_REFRESH_SKEW_SECONDS,
): boolean => {
  let token: string | null;
  let expirationValue: string | null;
  try {
    const storage = getSafeStorage();
    if (!storage) return false;
    token = storage.getItem(SESSION_KEYS.accessToken);
    expirationValue = storage.getItem(SESSION_KEYS.expiration);
  } catch {
    return false;
  }

  const expiration = Number(expirationValue || "");
  if (!token || !expiration) return true;

  const nowSeconds = Math.floor(Date.now() / 1000);
  return expiration - skewSeconds <= nowSeconds;
};
